import { doc, getDoc, collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore'
import { db } from './firebase'
import type { BonusDoc, DayDoc, DayQuestion, Theme } from './types'
import themes from '@data/themes.json'
import bank from '@data/questions.json'

export const LAUNCH_DATE = '2026-09-01'
export const BETA_START = '2026-08-26' // earliest playable date (pre-launch beta packs)

export function todayUtc(): string { return new Date().toISOString().slice(0, 10) }

export function dayNumber(date: string): number {
  return Math.floor((Date.parse(date + 'T00:00:00Z') - Date.parse(LAUNCH_DATE + 'T00:00:00Z')) / 86400000) + 1
}

export function themeForDate(date: string): Theme {
  const t = themes as Theme[]
  const n = dayNumber(date)
  return t[(((n - 1) % t.length) + t.length) % t.length]
}

/** Offline/dev fallback: build the static day pack from the bundled bank. */
export function localDay(date: string): DayDoc {
  const theme = themeForDate(date)
  const allQ = (bank as any[]).filter((q) => q.theme === theme.id && !q.bonus)
  const variants = Math.max(1, Math.floor(allQ.length / 10))
  const cycle = Math.floor((dayNumber(date) - 1) / (themes as Theme[]).length)
  const variant = ((cycle % variants) + variants) % variants
  const qs = allQ.slice(variant * 10, variant * 10 + 10)
    .map((q): DayQuestion => ({
      id: q.id, prompt: q.prompt, answer: q.answer, unit: q.unit, tier: q.tier,
      hero: q.hero, story: q.story, credit: q.credit, frozenAt: null,
    }))
  const hero = qs.find((q) => q.hero)!
  const rest = qs.filter((q) => q !== hero)
  const extended = rest.slice(4)
  if (extended.length) extended[extended.length - 1] = { ...extended[extended.length - 1], hero: true }
  return {
    date, number: dayNumber(date), theme,
    questions: [...rest.slice(0, 4), hero], extended,
    generatedAt: date + 'T00:00:00.000Z', local: true,
  }
}

/** ISO week id, e.g. "2026-W36" (Monday-start, UTC). */
export function isoWeek(date = todayUtc()): string {
  const d = new Date(date + 'T00:00:00Z')
  const day = (d.getUTCDay() + 6) % 7
  d.setUTCDate(d.getUTCDate() - day + 3)
  const year = d.getUTCFullYear()
  const jan4 = new Date(Date.UTC(year, 0, 4))
  const week = 1 + Math.round(((d.getTime() - jan4.getTime()) / 86400000 - 3 + ((jan4.getUTCDay() + 6) % 7)) / 7)
  return `${year}-W${String(week).padStart(2, '0')}`
}

export async function loadBonus(week = isoWeek()): Promise<BonusDoc | null> {
  try {
    const snap = await getDoc(doc(db, 'bonus', week))
    if (snap.exists()) return snap.data() as BonusDoc
  } catch (e) { console.warn('loadBonus failed', e) }
  return null
}

/** Render a bonus prompt's {when} relative to its freeze time: "yesterday", "3 days ago"… */
export function renderWhen(q: DayQuestion): string {
  if (!q.frozenAt || !q.prompt.includes('{when}')) return q.prompt
  let ref = new Date(q.frozenAt)
  if (q.tier === 'yesterday') ref = new Date(ref.getTime() - 86400000) // the completed day before the freeze
  const days = Math.floor((Date.parse(todayUtc() + 'T00:00:00Z') - Date.parse(ref.toISOString().slice(0, 10) + 'T00:00:00Z')) / 86400000)
  const rel = days <= 0 ? 'earlier today' : days === 1 ? 'yesterday' : `${days} days ago`
  const when = q.tier === 'live' ? `at 00:00 UTC ${days <= 0 ? 'today' : rel}` : rel
  return q.prompt.replace('{when}', when)
}

export async function loadDay(date: string): Promise<DayDoc> {
  try {
    const snap = await getDoc(doc(db, 'days', date))
    if (snap.exists()) return snap.data() as DayDoc
  } catch (e) {
    console.warn('loadDay failed, using local fallback', e)
  }
  return localDay(date)
}

export interface ArchiveEntry { date: string; number: number; theme: Theme }

export async function loadArchive(before: string, n = 60): Promise<ArchiveEntry[]> {
  try {
    const q = query(collection(db, 'days'), where('date', '<', before), orderBy('date', 'desc'), limit(n))
    const snap = await getDocs(q)
    return snap.docs.map((d) => { const x = d.data() as DayDoc; return { date: x.date, number: x.number, theme: x.theme } })
  } catch (e) {
    console.warn('loadArchive failed', e)
    // local fallback: every date since launch
    const out: ArchiveEntry[] = []
    for (let d = new Date(before + 'T00:00:00Z'); out.length < n; d = new Date(d.getTime() - 86400000)) {
      const date = d.toISOString().slice(0, 10)
      if (date < LAUNCH_DATE) break
      if (date < before) out.push({ date, number: dayNumber(date), theme: themeForDate(date) })
    }
    return out
  }
}
